import { useQuery } from "@tanstack/react-query";
import concat from "lodash/concat";
import { useMemo } from "react";
import { useProductCacheTimestamp } from "../../../legacy/hooks/use-product-cache-timestamp/useProductCacheTimestamp";
import useAccessToken from "../../../legacy/hooks/useAccessToken";
import type { IProductLean } from "../../../legacy/pages/ProductsListPage/entities/product-lean/product-lean.entity.interface";
import { productsService } from "../../../legacy/pages/ProductsListPage/services/products.service.instance";
import { productQueryKeys } from "./product.query-keys";
import type { IUseProductsLeanQuery } from "./useProductsLean.query.interface";

const CATALOG_STALE_TIME_MILLIS = 60_000;

export function useProductsLeanQuery(enabled: boolean = true): IUseProductsLeanQuery {
  const accessToken = useAccessToken();
  const [productCacheTimestamp] = useProductCacheTimestamp();
  const citizenId = accessToken?.citizen?.id;

  const query = useQuery({
    queryKey: productQueryKeys.lean(citizenId, productCacheTimestamp),
    queryFn: function fetchLeanCatalog() {
      return productsService.getAllProductsLeanWithAvailability(citizenId!, productCacheTimestamp);
    },
    enabled: enabled && !!citizenId && !!productCacheTimestamp,
    staleTime: CATALOG_STALE_TIME_MILLIS,
  });

  const orderedProducts = useMemo(
    function toOrderedProducts(): IProductLean[] {
      const catalog = query.data;
      if (!catalog) {
        return [];
      }
      return concat(catalog.productsAvailable, catalog.productsNotAvailable);
    },
    [query.data]
  );

  return {
    orderedProducts,
    loading: query.isInitialLoading,
    error: (query.error as Error) ?? null,
  };
}
